import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'

function Badge({ tone='gray', children }) {
  const map = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    purple: 'bg-purple-100 text-purple-800',
    teal: 'bg-teal-100 text-teal-800',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[tone]}`}>{children}</span>
}

function Card({ children }) { return <div className="card">{children}</div> }

const VENUES = {
  diggers: 'TwinStars Diggers',
  leagues: 'TwinStars Leagues Club',
  golf: 'TwinStars Golf Club',
  sports: 'TwinStars Sports Club',
}

function categoryTone(category) {
  if (category === 'risk') return 'purple'
  if (category === 'training') return 'blue'
  if (category === 'form') return 'teal'
  return 'gray'
}

function categoryLabel(category) {
  if (category === 'form') return 'Form'
  return category
}

function entityLabel(t) {
  if (t.category === 'form') {
    if (t.links?.subjectLabel) return t.links.subjectLabel
    if (t.links?.subjectType && t.links?.subjectId) return `${t.links.subjectType}: ${t.links.subjectId}`
    return 'Internal form'
  }
  if (t.level === 'group') return 'Group'
  return `Venue — ${VENUES[t.entity_id] || t.entity_id || '—'}`
}

export default function Tasks() {
  const navigate = useNavigate()
  const [filters, setFilters] = React.useState({ q:'', category:'all', level:'all', status:'all', mine:false })
  const [tasks, setTasks] = React.useState([])
  const fetchTasks = React.useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (filters.category !== 'all') params.set('category', filters.category)
      if (filters.level !== 'all') params.set('level', filters.level)
      if (filters.status !== 'all') params.set('status', filters.status)
      if (filters.q) params.set('q', filters.q)
      if (filters.mine) params.set('mine', '1')
      const r = await authFetch(getLocalApiUrl(`/api/governance/tasks?${params.toString()}`))
      const j = await r.json()
      if (j.success) setTasks(j.data)
    } catch {}
  }, [filters])
  React.useEffect(() => { fetchTasks() }, [fetchTasks])

  const filtered = tasks

  const toneFor = (status) => status==='completed' ? 'green' : status==='overdue' ? 'red' : status==='due' ? 'yellow' : 'blue'

  const openTask = (t) => {
    if (t.category === 'form') {
      const links = typeof t.links === 'string'
        ? (() => { try { return JSON.parse(t.links) } catch { return null } })()
        : t.links
      const href = links?.href
        || (links?.formAssignmentId ? `/compliance-safe/forms/assignments/${links.formAssignmentId}` : null)
      if (href) {
        navigate(href)
        return
      }
      window.alert('This form task is missing its assignment link. Re-assign the form from the record to create a new task.')
      return
    }
    if (t.category==='risk' && t.level==='venue') {
      const qp = new URLSearchParams()
      qp.set('venue', t.entity_id)
      if (t.period) qp.set('period', t.period)
      if (t.type) qp.set('type', t.type)
      navigate(`/governance-safe/risk?${qp.toString()}`)
    } else if (t.category==='checklist' && t.level==='venue') {
      const qp = new URLSearchParams()
      qp.set('venue', t.entity_id)
      if (t.period) qp.set('period', t.period)
      if (t.type) qp.set('type', t.type)
      navigate(`/governance-safe/checklist?${qp.toString()}`)
    } else if (t.category==='training') {
      navigate(`/governance-safe/training`)
    } else {
      navigate(`/governance-safe/dashboard`)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Governance Tasks</h2>
        <p className="text-sm text-gray-500 mt-1">One-time, periodic and event-driven tasks across Group and Venues</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          className="flex-1 min-w-[180px] max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-psg-teal focus:border-transparent"
          placeholder="Search tasks"
          value={filters.q}
          onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
        />
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap px-1">
          <input
            type="checkbox"
            checked={filters.mine}
            onChange={(e) => setFilters((p) => ({ ...p, mine: e.target.checked }))}
          />
          My tasks
        </label>
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white min-w-[9.5rem] focus:outline-none focus:ring-2 focus:ring-psg-teal"
          value={filters.category}
          onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
        >
          <option value="all">All Categories</option>
          <option value="checklist">Checklist</option>
          <option value="risk">Risk</option>
          <option value="training">Training</option>
          <option value="form">Form</option>
        </select>
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white min-w-[7.5rem] focus:outline-none focus:ring-2 focus:ring-psg-teal"
          value={filters.level}
          onChange={(e) => setFilters((p) => ({ ...p, level: e.target.value }))}
        >
          <option value="all">All Levels</option>
          <option value="group">Group</option>
          <option value="venue">Venue</option>
        </select>
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white min-w-[7.5rem] focus:outline-none focus:ring-2 focus:ring-psg-teal"
          value={filters.status}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="due">Due</option>
          <option value="overdue">Overdue</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-gray-500 uppercase text-xs">
                <th className="text-left p-3">Task</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Level / Entity</th>
                <th className="text-left p-3">Period</th>
                <th className="text-left p-3">Due</th>
                <th className="text-left p-3">Assignee</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.task_id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-900">
                    <div>{t.title}</div>
                    {t.task_id ? <div className="text-[11px] text-gray-400 font-normal mt-0.5">{t.task_id}</div> : null}
                  </td>
                  <td className="p-3"><Badge tone={categoryTone(t.category)}>{categoryLabel(t.category)}</Badge></td>
                  <td className="p-3">{entityLabel(t)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {t.period && <span className="text-gray-600">{t.period}</span>}
                    </div>
                  </td>
                  <td className="p-3">{t.due_date || '—'}</td>
                  <td className="p-3">{t.assignee || '—'}</td>
                  <td className="p-3"><Badge tone={toneFor(t.status)}>{t.status}</Badge></td>
                  <td className="p-3 text-right">
                    <button type="button" className="btn-primary" onClick={()=>openTask(t)}>
                      {t.category === 'form' ? 'Open form' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length===0 && (
                <tr>
                  <td className="p-6 text-center text-gray-500" colSpan={8}>No tasks</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
